// S15 — Media Center page (two-pane: folder tree + file grid/list).
//
// Responsive:
//   • Desktop (>= md): persistent left sidebar with folder tree.
//   • Mobile (< md):   folder tree opens in a Drawer via "Klasörler" button.

import { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Drawer,
  Grid,
  Input,
  Row,
  Segmented,
  Space,
  Typography,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@shared/hooks/useDebounce';
import { FolderTree, Grid3x3, List as ListIcon, RefreshCw } from 'lucide-react';
import { PageHeader } from '@shared/components/PageHeader';
import { filesApi, type FileItem } from '@api/files';
import { ROUTES } from '@shared/constants/routes';
import { FolderTreeSidebar } from '../components/FolderTreeSidebar';
import { FileList } from '../components/FileList';
import { FileUploader } from '../components/FileUploader';
import { FileDetailDrawer } from '../components/FileDetailDrawer';

const { Text } = Typography;

export function MediaCenterPage() {
  const { t } = useTranslation(['media', 'common']);
  const navigate = useNavigate();
  const { folderId: folderIdParam } = useParams<{ folderId?: string }>();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [folderDrawerOpen, setFolderDrawerOpen] = useState(false);

  const selectedFolderId = folderIdParam ?? null;

  const setSelectedFolderId = (id: string | null) => {
    setPage(1);
    if (id) void navigate(`/media/folder/${id}`);
    else void navigate(ROUTES.MEDIA);
    if (isMobile) setFolderDrawerOpen(false);
  };

  const filesQ = useQuery({
    queryKey: [
      'files',
      'list',
      { folderId: selectedFolderId, search: debouncedSearch, page, pageSize },
    ],
    queryFn: () =>
      filesApi.list({
        folderId: selectedFolderId ?? undefined,
        search: debouncedSearch || undefined,
        page,
        pageSize,
      }),
    staleTime: 30_000,
  });

  const items = useMemo(() => filesQ.data?.items ?? [], [filesQ.data]);
  const total = filesQ.data?.total ?? 0;

  const sidebar = (
    <FolderTreeSidebar
      selectedFolderId={selectedFolderId}
      onSelect={(id) => setSelectedFolderId(id)}
    />
  );

  return (
    <div data-testid="media-center-page">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        breadcrumb={[
          { label: t('breadcrumb.dashboard'), to: ROUTES.DASHBOARD },
          { label: t('breadcrumb.media') },
        ]}
        actions={
          <Space wrap>
            {isMobile ? (
              <Button
                icon={<FolderTree size={14} />}
                onClick={() => setFolderDrawerOpen(true)}
                data-testid="media-open-folders"
              >
                {t('toolbar.folders')}
              </Button>
            ) : null}
            <Button
              icon={<RefreshCw size={14} />}
              onClick={() => void filesQ.refetch()}
              loading={filesQ.isFetching}
              aria-label={t('toolbar.refresh')}
            >
              {t('toolbar.refresh')}
            </Button>
          </Space>
        }
      />

      <Row gutter={[12, 12]}>
        {!isMobile ? (
          <Col xs={24} md={7} lg={6} xl={5}>
            <Card
              size="small"
              styles={{ body: { padding: 12, height: 'calc(100vh - 220px)' } }}
            >
              {sidebar}
            </Card>
          </Col>
        ) : null}
        <Col xs={24} md={17} lg={18} xl={19}>
          <Card size="small" styles={{ body: { padding: 12 } }}>
            <Space
              wrap
              style={{
                width: '100%',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <Input.Search
                allowClear
                placeholder={t('toolbar.searchPlaceholder')}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{ maxWidth: 320 }}
                data-testid="file-search"
              />
              <Space wrap>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('toolbar.fileCount', { value: total.toLocaleString('tr-TR') })}
                </Text>
                <Segmented
                  size="small"
                  value={view}
                  onChange={(v) => setView(v as 'grid' | 'list')}
                  options={[
                    { value: 'grid', icon: <Grid3x3 size={14} /> },
                    { value: 'list', icon: <ListIcon size={14} /> },
                  ]}
                />
              </Space>
            </Space>

            <FileUploader folderId={selectedFolderId} />

            <div style={{ marginTop: 12 }}>
              <FileList
                items={items}
                loading={filesQ.isLoading}
                view={view}
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={(p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                }}
                onSelect={(f) => setSelectedFile(f)}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Drawer
        open={folderDrawerOpen}
        onClose={() => setFolderDrawerOpen(false)}
        placement="left"
        width={Math.min(320, window.innerWidth - 32)}
        title={t('toolbar.folders')}
        styles={{ body: { padding: 12 } }}
      >
        {sidebar}
      </Drawer>

      <FileDetailDrawer
        file={selectedFile}
        open={selectedFile !== null}
        onClose={() => setSelectedFile(null)}
      />
    </div>
  );
}

export default MediaCenterPage;
